/**
 * The one place the app asks "what roles does this signed-in person hold".
 *
 * WHY THIS EXISTS: the hat switcher and the dashboard were deciding from
 * DIFFERENT role sets. ProfileViewContext fed useProfileView only
 * `tenant?.roles` (the JWT claim), while the dashboard used the wider
 * resolveRoles union. So a role present on the org membership but not yet on
 * the claim would give someone a dashboard with no matching hat to select —
 * two components disagreeing about who you are.
 *
 * Both now call this. One source, one call site.
 */

import { useMemo } from 'react';
import type { Role } from '@kantera/types';
import { useAuth } from './useAuth';
import { resolveRoles } from '../utils/userRoles';

export function useResolvedRoles(): Role[] {
  const { tenant, activeOrg } = useAuth();
  return useMemo(
    () =>
      resolveRoles({
        tenantRoles: tenant?.roles as ReadonlyArray<Role> | undefined,
        orgRoles: activeOrg?.roles as ReadonlyArray<Role> | undefined,
        // Law 1: the membership counts only when it is provably the same org
        // the verified token names.
        tenantOrgId: tenant?.orgId,
        orgRolesOrgId: activeOrg?.orgId,
      }),
    [tenant?.roles, tenant?.orgId, activeOrg?.roles, activeOrg?.orgId],
  );
}
